import React, { useState, useEffect, useCallback } from 'react';

import { Icon } from 'selfly-ui-react';
import Input from 'selfly-ui-react/dist/components/Input';

const prefix = process.env.REACT_APP_SE_PREFIX || 'se';

export default function DataTable(props) {
    const { columns, className, activeItem, data, searchEnabled, filterEnabled, afterFilter } = props;

    const [activeFilter, setActiveFilter] = useState(null);
    const [order, setOrder] = useState(false);

    const [currentData, setCurrentData] = useState([]);

    const [searchKeys, setSearchKeys] = useState({});

    const [filteredColumnsCount, setFilteredColumnsCount] = useState(0);

    const sort = useCallback(
        origData => {
            if (!activeFilter || !origData.length) return [...origData];
            const data = [...origData];

            const column = columns.find(c => c.key === activeFilter);
            const value = data[0][activeFilter] ? data[0][activeFilter] : column.defaultValue !== undefined ? column.defaultValue : '';

            const type = typeof value;
            const isDateType = isValueDate(value);

            const toOrder = arr => {
                return order ? arr : arr.reverse();
            };

            const sortNumbers = (a, b) => {
                return !isDateType ? b[activeFilter] - a[activeFilter] : Date.parse(b[activeFilter]) - Date.parse(a[activeFilter]);
            };

            const sortStrings = (a, b) => {
                return (a[activeFilter] || '').localeCompare(b[activeFilter] || '');
            };

            const sortBoolean = (a, b) => {
                return a[activeFilter] === b[activeFilter] ? 0 : a[activeFilter] ? -1 : 1;
            };

            if (column.sortFn !== undefined && typeof column.sortFn === 'function') {
                return toOrder(data.slice().sort(column.sortFn));
            }

            if (isDateType) return toOrder(data.slice().sort(sortNumbers));

            switch (type) {
                case 'string':
                    return toOrder(data.slice().sort(sortStrings));
                case 'number':
                    return toOrder(data.slice().sort(sortNumbers));
                case 'boolean':
                    return toOrder(data.slice().sort(sortBoolean));
                default:
                    return data.slice();
            }
        },
        [columns, activeFilter, order]
    );

    useEffect(() => {
        if (Array.isArray(data)) {
            const dataSorted = sort(
                filterEnabled === true
                    ? data.filter(item => {
                          const res = itemMatchesFilters(item, searchKeys);
                        //   res === false && console.log('ITEM', item, searchKeys);
                          return res;
                      })
                    : data
            );
            setCurrentData(dataSorted);
            if (typeof afterFilter === 'function') {
                afterFilter(dataSorted);
            }
        }
    }, [data, searchEnabled, filterEnabled, afterFilter, activeFilter, order, searchKeys, sort]);

    useEffect(() => {
        if (filterEnabled) {
            const filteredColumns = columns.reduce((tot, col) => {
                if (col.filterEnabled) {
                    tot++;
                }
                return tot;
            }, 0);

            setFilteredColumnsCount(filteredColumns);
        } else {
            setFilteredColumnsCount(0);
        }
    }, [columns, filterEnabled]);

    function setFilterName(filterName) {
        if (activeFilter === filterName && activeFilter !== null) {
            setOrder(prev => !prev);
        } else {
            setActiveFilter(filterName);
            setOrder(true);
        }
    }

    function itemMatchesFilters(item, filters) {
        for (const key in filters) {
            const itemValue = item[key];
            const valueType = typeof itemValue;
            if (
                valueType !== 'string' ||
                (valueType === 'string' && typeof filters[key] === 'string' && !itemValue.toUpperCase().includes(filters[key].toUpperCase()))
            ) {
                return false;
            }
        }
        return true;
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <table className={`se-table ${className !== undefined ? className : ''}`}>
                <TableHeader
                    columns={columns}
                    activeFilter={activeFilter}
                    setFilterName={setFilterName}
                    filters={searchKeys}
                    setFilters={setSearchKeys}
                    searchEnabled={filteredColumnsCount > 0}
                />
                <TableBody
                    columns={columns}
                    content={currentData}
                    activeItem={activeItem}
                    rowOnClick={typeof props.rowOnClick === 'function' ? props.rowOnClick : false}
                />
            </table>
        </div>
    );
}

function TableHeader(props) {
    const { columns, setFilterName, activeFilter, searchEnabled } = props;

    const [filters, setFilters] = useState(props.filters);

    const [delay, setDelay] = useState(null);

    function updateFilters(key, value) {
        const nd = { ...filters };
        if (value === '') {
            delete nd[key];
        } else {
            nd[key] = value;
        }

        setFilters(nd);

        if (typeof props.setFilters === 'function') {
            if (delay !== null) {
                clearTimeout(delay);
            }

            setDelay(
                setTimeout(() => {
                    props.setFilters(nd);
                }, 750)
            );
        }
    }

    return (
        <thead className="se-table-head">
            {searchEnabled && (
                <tr>
                    {columns.map((col, i) => {
                        if (col.filterEnabled === false) return <th key={col.key} className={col.headerClassName || ''}></th>;
                        return (
                            <th key={col.key} className={col.headerClassName || ''}>
                                <Input
                                    placeholder={`Filter column ${col.header}`}
                                    size="sm"
                                    value={filters[col.key] ? filters[col.key] : ''}
                                    onChange={e => {
                                        updateFilters(col.key, e.target.value);
                                    }}
                                />
                            </th>
                        );
                    })}
                </tr>
            )}

            <tr>
                {columns.map((col, i) => {
                    if (col.sorting === false) {
                        return (
                            <th
                                key={`header-${i}`}
                                // className={ activeFilter === col.key ? "se-table-head--active" : undefined }
                                className={col.headerClassName}
                                width={col.width !== undefined ? col.width : 'auto'}>
                                <div className="se-table-head-content">{col.header}</div>
                            </th>
                        );
                    }

                    const classes = [col.headerClassName, activeFilter === col.key ? 'se-table-head--active' : undefined].join(' ');
                    return (
                        <th
                            key={`header-${i}`}
                            className={classes} //activeFilter === col.key ? 'se-table-head--active' : undefined}
                            width={col.width !== undefined ? col.width : 'auto'}
                            onClick={() => {
                                setFilterName(col.key);
                            }}>
                            <div className="se-table-head-content">
                                {col.header}
                                <Icon size="sm">sort</Icon>
                            </div>
                        </th>
                    );
                })}
            </tr>
        </thead>
    );
}

function TableBody(props) {
    const { columns, content, rowOnClick, activeItem } = props;

    return (
        <tbody>
            {content.map((row, rowIndex) => (
                <TableRow activeItem={activeItem} data={row} columns={columns} key={rowIndex} rowNo={rowIndex} rowOnClick={rowOnClick} />
            ))}
        </tbody>
    );
}

function TableRow(props) {
    const { data, columns, rowOnClick, activeItem, rowNo } = props;

    const rowClick = () => {
        if (rowOnClick !== false) {
            rowOnClick(data, rowNo);
        }
    };

    const classNames = [
        `${prefix}-table-item`,
        rowOnClick && `${prefix}-table-item--clickable`,
        activeItem != null && rowNo === activeItem && `${prefix}-table-item--active`,
    ].join(' ');
    return (
        <tr className={classNames} onClick={rowClick}>
            {columns.map((column, colIndex) => (
                <TableCell rowData={data} column={column} key={`row-${colIndex}`} rowNo={props.rowNo} />
            ))}
        </tr>
    );
}

function TableCell(props) {
    const { column, rowData } = props;

    const classes =
        column.className !== undefined
            ? typeof column.className === 'string'
                ? column.className
                : Array.isArray(column.className)
                ? column.className.join(' ')
                : ''
            : '';

    if (typeof column.key === 'function') {
        return <td className={classes}>{column.key(rowData, column, props.rowNo)}</td>;
    }

    const data = rowData[column.key];

    const value = data !== undefined ? data : column.defaultValue !== undefined ? column.defaultValue : '';

    const cellOnClick = e => {
        if (typeof column.onClick === 'function') {
            const bubble = column.onClick(data, rowData);
            if (!bubble) e.stopPropagation();
        }
    };

    // Custom function for value visualization
    if (column.content !== undefined && typeof column.content === 'function') {
        return (
            <td className={classes} onClick={cellOnClick}>
                {column.content(value, rowData, column)}
            </td>
        );
    }

    return (
        <td className={classes} onClick={cellOnClick}>
            <TableCellContents data={value} column={column} />
        </td>
    );
}

function TableCellContents(props) {
    const type = props.column.type ? props.column.type : typeof props.data;

    // Ugly date formatter hack to remove the moment.js This may break...
    function leadingZeroPad(value) {
        if (value >= 10) {
            return `${value}`;
        }
        return `0${value}`;
    }

    function DateFormatter(data, type) {
        const d = new Date(data);
        const darr = [
            d.getFullYear(),
            leadingZeroPad(d.getMonth() + 1),
            leadingZeroPad(d.getDate()),
            leadingZeroPad(d.getHours()),
            leadingZeroPad(d.getMinutes()),
            leadingZeroPad(d.getSeconds()),
            leadingZeroPad(d.getMilliseconds()),
        ];

        if (type === 'time') {
            return `${darr[3]}:${darr[4]}`;
        }
        if (type === 'date') {
            return `${darr[2]}.${darr[1]}.${darr[0]}`;
        }
        if (type === 'datetime') {
            return `${darr[2]}.${darr[1]}.${darr[0]} ${darr[3]}:${darr[4]}`;
        }
    }

    switch (type) {
        case 'boolean':
            return props.data ? (
                <Icon color="blue" size="md">
                    check_box
                </Icon>
            ) : (
                <Icon color="blue" size="md">
                    check_box_outline_blank
                </Icon>
            );
        case 'euro':
            return `${Number.parseFloat(props.data).toFixed(2)} €`;
        case 'dollar':
            return `$${Number.parseFloat(props.data).toFixed(2)}`;
        case 'money':
            return `${Number.parseFloat(props.data).toFixed(2)}`;
        case 'time':
            return DateFormatter(props.data, 'time');

        case 'date':
            return DateFormatter(props.data, 'date');
        case 'datetime':
            return DateFormatter(props.data, 'datetime');
        default:
            return props.data;
    }
}

function isValueDate(value) {
    const valueType = typeof value;
    if (valueType === 'object') {
        if (value === null) {
            return false;
        }

        if (value.getMonth !== undefined) {
            return true;
        }
        return false;
    }
    if (valueType === 'string') {
        return !isNaN(Date.parse(value));
    }
    return false;
}

// DataTable.propTypes = {
//   /** Item to be rendered as active, value should correspond to dataKey */
//   activeItem: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
//   data: PropTypes.arrayOf(PropTypes.object).isRequired,
//   identifier: PropTypes.string.isRequired,
//   rowOnClick: PropTypes.func,
//   className: PropTypes.string,
//   columns: PropTypes.arrayOf(
//     PropTypes.shape({
//       key: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.number]).isRequired,
//       header: PropTypes.string.isRequired,
//       type: PropTypes.string,
//       className: PropTypes.string,
//       width: PropTypes.string,
//       content: PropTypes.func,
//       sortFn: PropTypes.func,
//       defaultValue: PropTypes.any,
//       sorting: PropTypes.bool,
//       onClick: PropTypes.func,
//     })
//   ).isRequired,
// };

// DataTable.defaultProps = {
//   activeItem: null,
//   data: [],
//   identifier: "id",
// };
